import React, { useMemo } from "react";

// PayoffTable — tabular companion to PayoffChart. Shows P&L at a sampled set
// of stock prices (spot ±25%) using the same `payoff_curve` the chart consumes.
// Toggle between $ and % via the `mode` prop ('dollars' | 'percent').
const fmt$ = (v) =>
  Number.isFinite(v)
    ? `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    : "—";
const fmtPct = (v) => (Number.isFinite(v) ? `${v >= 0 ? "+" : ""}${v.toFixed(1)}%` : "—");

const PayoffTable = ({
  curve = [],
  spot,
  breakevens = [],
  netPremium = 0,
  maxLoss = null,
  mode = "dollars",
}) => {
  const rows = useMemo(() => {
    if (!curve.length) return [];
    // Sample ±25% range around spot at 2.5% increments + always include
    // breakevens, spot, and the extrema.
    const includeSet = new Set();
    if (Number.isFinite(spot)) {
      for (let p = -25; p <= 25; p += 2.5) {
        includeSet.add(spot * (1 + p / 100));
      }
      includeSet.add(spot);
    }
    breakevens.forEach((b) => Number.isFinite(b) && includeSet.add(b));
    const targets = Array.from(includeSet).sort((a, b) => a - b);

    // Map each target to the nearest payoff_curve sample.
    return targets.map((px) => {
      let best = curve[0];
      let bestDiff = Math.abs(px - curve[0].price);
      for (const pt of curve) {
        const d = Math.abs(px - pt.price);
        if (d < bestDiff) { best = pt; bestDiff = d; }
      }
      return { price: best.price, pnl: best.pnl };
    });
  }, [curve, spot, breakevens]);

  // Denominator for % P/L: max loss magnitude (capital at risk).
  // Fall back to abs(netPremium) when max loss is undefined (e.g., naked).
  const riskBasis = Math.abs(maxLoss ?? netPremium ?? 0);
  const showPct = mode === "percent" && riskBasis > 0;

  if (!rows.length) {
    return (
      <div
        className="bg-[#0E1A30] border border-white/10 rounded-xl p-6 text-center text-white/45 text-[13px]"
        data-testid="forge-payoff-table-empty"
      >
        Build a strategy to see the payoff table.
      </div>
    );
  }

  return (
    <div
      className="bg-[#0E1A30] border border-white/10 rounded-xl overflow-hidden"
      data-testid="forge-payoff-table"
    >
      <div className="px-4 py-2.5 border-b border-white/10 text-[11px] uppercase tracking-[0.22em] text-white/60 flex items-center justify-between">
        <span>Payoff at Expiry · {showPct ? "% of risk" : "USD"}</span>
        {Number.isFinite(spot) && (
          <span className="text-white/40 num normal-case">spot ${spot.toFixed(2)}</span>
        )}
      </div>
      <div className="overflow-auto max-h-[480px]">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 bg-[#0E1A30] text-[10px] uppercase tracking-[0.18em] text-white/40 border-b border-white/5">
            <tr>
              <th className="text-left py-2 px-3">Stock Price</th>
              <th className="text-right py-2 px-3">Move</th>
              <th className="text-right py-2 px-3">P/L {showPct ? "%" : "$"}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const movePct = Number.isFinite(spot) && spot > 0 ? ((r.price / spot) - 1) * 100 : null;
              const pct = riskBasis > 0 ? (r.pnl / riskBasis) * 100 : null;
              const isSpotRow = Number.isFinite(spot) && Math.abs(r.price - spot) < spot * 0.001;
              const isBe = breakevens.some((b) => Math.abs(r.price - b) < spot * 0.005);
              return (
                <tr
                  key={`${r.price}-${i}`}
                  className={`border-b border-white/[0.04] ${
                    isSpotRow ? "bg-[#3B82F6]/[0.10]" : isBe ? "bg-[#FACC15]/[0.06]" : ""
                  }`}
                  data-testid={`forge-payoff-row-${i}`}
                >
                  <td className="py-1.5 px-3 num text-white">
                    {fmt$(r.price)}
                    {isSpotRow && <span className="ml-2 text-[9px] uppercase tracking-[0.18em] text-[#60A5FA]">spot</span>}
                    {isBe && !isSpotRow && <span className="ml-2 text-[9px] uppercase tracking-[0.18em] text-[#FACC15]">B/E</span>}
                  </td>
                  <td className={`py-1.5 px-3 text-right num ${movePct >= 0 ? "text-white/55" : "text-white/55"}`}>
                    {movePct == null ? "—" : fmtPct(movePct)}
                  </td>
                  <td className={`py-1.5 px-3 text-right num font-medium ${
                    r.pnl >= 0 ? "text-[#22C55E]" : "text-[#EF4444]"
                  }`}>
                    {showPct ? (pct == null ? "—" : fmtPct(pct)) : fmt$(r.pnl)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 border-t border-white/10 text-[10px] text-white/35 num">
        Sampled at 2.5% intervals around spot · breakevens highlighted in yellow · spot row in blue.
      </div>
    </div>
  );
};

export default PayoffTable;
